import { useState, useEffect } from 'react';
import { ExternalLink, Loader2, User, Mail, Phone, Calendar, DollarSign, Check } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { 
  ConnectComponentsProvider, 
  ConnectBalances
} from '@stripe/react-connect-js';
import StripeConnectBanner from './StripeConnectBanner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const RevenueTab = () => {
  const [stripeConnectInstance, setStripeConnectInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  useEffect(() => {
    checkStripeStatus();
  }, []);

  const loadTransactions = async () => {
    try {
      setLoadingTransactions(true);
      const response = await api.get('/organizations/stripe-connect/transactions?limit=50');
      console.log('Stripe transactions response:', response.data);
      console.log('First transaction:', response.data.transactions?.[0]);
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Error loading transactions from Stripe:', error);
      toast.error('Failed to load transaction data');
    } finally {
      setLoadingTransactions(false);
    }
  };

  const checkStripeStatus = async () => {
    try {
      const statusRes = await api.get('/organizations/stripe-connect/status');
      setStatus(statusRes.data);
      
      if (statusRes.data?.onboardingComplete) {
        await initializeStripe();
        await loadTransactions(); // Load from Stripe!
      }
    } catch (error) {
      console.log('Payment processing not set up yet');
    } finally {
      setLoading(false);
    }
  };

  const initializeStripe = async () => {
    try {
      const instance = await loadConnectAndInitialize({
        publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
        fetchClientSecret: async () => {
          const res = await api.post('/organizations/stripe-connect/account-session');
          return res.data.clientSecret;
        },
        appearance: {
          variables: {
            colorPrimary: '#007AFF',
          },
        },
      });
      setStripeConnectInstance(instance);
    } catch (err) {
      console.log('Could not initialize payment components');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Setup Banner (if not connected) - ONLY show this */}
      {!status?.onboardingComplete && (
        <StripeConnectBanner />
      )}

      {/* Show Stripe components ONLY if connected */}
      {status?.onboardingComplete && stripeConnectInstance && (
        <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
          {/* Header with Payment Status and View Dashboard Link */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-600">Payment Processing Active</span>
            </div>
            <a
              href={status?.accountId ? `https://dashboard.stripe.com/${status.accountId}` : "https://dashboard.stripe.com"}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              View Full Dashboard
            </a>
          </div>

          {/* Balance Component (from Stripe) */}
          <div className="bg-white rounded-lg p-4 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.125)]">
            <ConnectBalances />
          </div>

          {/* Customer Transactions List (FROM STRIPE - Source of Truth!) */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingTransactions ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-10 h-10 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">No transactions yet</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {transactions.map((transaction) => {
                    console.log('Rendering transaction:', transaction);
                    return (
                    <div
                      key={transaction.id}
                      className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-200 hover:border-blue-300 transition-all hover:shadow-sm"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <User className="w-4 h-4 text-blue-600" />
                            <p className="font-bold text-gray-900">
                              {transaction.customerName || transaction.customerEmail || 'Guest Customer'}
                            </p>
                          </div>
                          {transaction.customerEmail && (
                            <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                              <Mail className="w-3 h-3" />
                              <span>{transaction.customerEmail}</span>
                            </div>
                          )}
                          {transaction.customerPhone && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Phone className="w-3 h-3" />
                              <span>{transaction.customerPhone}</span>
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600 text-xl">
                            ${transaction.amount.toFixed(2)}
                          </p>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            transaction.status === 'succeeded' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {transaction.status}
                          </span>
                        </div>
                      </div>
                      <div className="pt-3 border-t border-gray-200 space-y-1">
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Calendar className="w-3 h-3 text-gray-500" />
                          <span className="font-medium">{transaction.serviceName}</span>
                        </div>
                        {transaction.bookingDate && (
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <span>{transaction.bookingDate}</span>
                            {transaction.bookingTime && (
                              <>
                                <span>•</span>
                                <span>{transaction.bookingTime}</span>
                              </>
                            )}
                          </div>
                        )}
                        {transaction.confirmationCode && (
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">
                              {transaction.confirmationCode}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </ConnectComponentsProvider>
      )}
    </div>
  );
};

export default RevenueTab;
